import { inArray, isNotNull } from "drizzle-orm"
import {
  pgSchema,
  boolean,
  uniqueIndex,
  timestamp,
  jsonb,
  text,
  varchar,
  index,
  integer,
  decimal,
  unique,
} from "drizzle-orm/pg-core"
import { id_length, ulidId } from "./shared"

export const exportCertificateSchema = pgSchema("ec")

/**
 * T1 export certificate model.
 *
 * These two tables are only used for certificates raised by SalesForce.
 */
// For now we assume the export certificate requests are very specific to SalesForce.
// We don't know yet how the DFE will raise certificates.
// Probably very similar though.
export const exportCertificate = exportCertificateSchema.table(
  "export_certificate",
  {
    id: ulidId(),
    status: text("status", {
      // This enum needs to match the ExportCertificateStatus enum in the graphql
      enum: [
        "DRAFT",
        "RAISED",
        "CANCELLED",
        "AMENDED",
        "VERIFIED",
        "PROVISIONALLY_APPROVED",
        "APPROVED",
        "ACKNOWLEDGED",
        "ACCEPTED",
        "REQUESTED_TO_REVOKE",
        "REQUESTED_TO_RESUBMIT",
        "REQUESTED_TO_REPLACE",
        "PENDING_REPLACEMENT",
        "REVOKED",
        "REPLACED",
        "RESUBMITTED",
        "REPLACEMENT_AUTHORISED",
      ],
    }).notNull(),
    certificate_type: text("certificate_type", {
      enum: [
        "FREE_SALE",
        "FREE_SALE_STATEMENT",
        "LIVE_ANIMALS",
        "ORGANICS",
        "EXPORT_ELIGIBILITY_STATEMENT",
        "SUPPLEMENTARY",
        "SANITARY",
        "HALAL",
        "CATCH",
        "PHYTOSANITARY",
        "REEXPORT_PHYTOSANITARY",
        "OECD_SEED_VARIETAL",
        "NONCONSIGNMENT",
        "PRODUCT_ESTABLISHMENT_REGISTRATION_DOCUMENT",
        "OTHER_DOCUMENT",
      ],
    }).notNull(),
    destination_market: text("destination_market").notNull(),
    commodity: text("commodity"),
    requestor: text("requestor").notNull().default("sf"),
    requestor_id: text("requestor_id"),
    consignment: jsonb("consignment").notNull(),
    certificate_id: text("certificate_id").default("").notNull(),
    verifiers: jsonb("verifiers").default("[]").notNull(),
    certifier: jsonb("certifier"),
    state_machine_execution_arn: varchar("state_machine_execution_arn", {
      length: 1024,
    }),
    verification_task_token: text("verification_task_token"),
    approval_task_token: text("approval_task_token"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
    issue_date: timestamp("issue_date").defaultNow().notNull(),
    reason_for_status_change: text("reason_for_status_change")
      .default("")
      .notNull(),
    verification_needed: boolean("verification_needed").notNull().default(true),
  },
  (table) => {
    return {
      requestor: uniqueIndex("ec_requestor_id")
        .on(table.requestor, table.requestor_id)
        .where(isNotNull(table.requestor_id)),
    }
  },
)

export const exportCertificateItem = exportCertificateSchema.table(
  "export_certificate_line_item",
  {
    id: ulidId(),
    requestor: text("requestor"),
    requestor_id: text("requestor_id"),
    exportCertificate: varchar("export_certificate_id", { length: id_length })
      .notNull()
      .references(() => exportCertificate.id, { onDelete: "cascade" }),
    description: text("description").notNull(),
    common_name: text("common_name").notNull().default(""),
    line_item: jsonb("line_item").notNull(),
    verified: boolean("verified").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
    comments: text("comments").notNull().default(""),
  },
  (table) => {
    return {
      requestor: uniqueIndex("ec_requestor_product_id")
        .on(table.requestor, table.requestor_id)
        .where(isNotNull(table.requestor_id)),
    }
  },
)

// T3 (DFE) TC Core Model starts

/**
 * A single export certificate request.
 *
 * We insert this row just once. It contains request data the user
 * cannot modify, and also does not change. So although we update this
 * table, the intention is never to overwrite a previous value, only to
 * set it for the first and only time.
 */
const certificateRequestIdRef = () => certificateRequest.id
export const certificateRequest = exportCertificateSchema.table(
  "certificate_request",
  {
    id: ulidId(),
    requestorPartyId: text("requestor_party_id").notNull().default("00000000"), // ID of party which created this request. This is the same ID as consignment.consignor.id, except when a request is created on behalf of a consignor.
    requestorRegistrationId: varchar("requestor_registration_id", {
      length: id_length, // could have foreign key on party.registration
    }), // registrationId of party which created this request. This is the same as request.registrationId, except when a request is created on behalf of a consignor.
    productGroupId: varchar("product_item_group_id", {
      length: id_length,
    })
      .references(() => productGroup.id, { onDelete: "cascade" })
      .notNull(),
    /* Future:
    replacesCertificateRequest: varchar("replaces_certificate_request_id", { length: id_length })
      .references(() => certificateRequest.id, { onDelete: "cascade" }), */
    sfInstance: text("sf_instance").notNull().default("sf"),
    sfApplicationId: text("sf_application_id"),
    stateMachineExecutionArn: text("state_machine_execution_arn"),
    // Use when we are in the wait for verification or certification step
    taskToken: text("task_token"),
    certifier: jsonb("certifier"),
    // The certificate id is generated when the request is raised (send to MPI)
    certificateNumber: text("certificate_number"),
    // If verification is not needed, set this on the request,
    // at the time the request is raised. We use thot to check forbid
    // invalid status changes.
    requestorRegistrationNumber: text("requestor_registration_number"),
    verificationNeeded: boolean("verification_needed").notNull().default(true),
    replacedById: varchar("replaced_by_id", {
      length: id_length,
    }).references(certificateRequestIdRef, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by"),
    createdById: text("created_by_id"), // sf_contact_id for B2C Users and tc_user_id for AD Users
    modifiedAt: timestamp("modified_at").defaultNow().notNull(),
  },
  (table) => {
    return {
      sfApplication: uniqueIndex("sf_application_id")
        .on(table.sfApplicationId, table.sfInstance)
        .where(isNotNull(table.sfApplicationId)),
      // We do not support sharing a consignment among different requests
      oneConsignmentPerCertificateRequest: uniqueIndex(
        "certificate_request_product_group",
      ).on(table.productGroupId),
      replacedByIdIdx: index("replaced_by_id").on(table.replacedById),
    }
  },
)

/**
 * Every change a user makes to a request, causes a new row to be
 * inserted into this table.
 */
export const certificateRequestRevision = exportCertificateSchema.table(
  "certificate_request_revision",
  {
    id: ulidId(),
    certificateRequestId: varchar("certificate_request_id", {
      length: id_length,
    })
      .notNull()
      .references(() => certificateRequest.id, { onDelete: "cascade" }),
    // This should match the enum in template.graphql.
    // We allow the user to set or change the certificate type at any
    // time during the draft.
    certificateType: text("certificate_type", {
      enum: [
        "FREE_SALE",
        "FREE_SALE_STATEMENT",
        "LIVE_ANIMALS",
        "ORGANICS",
        "EXPORT_ELIGIBILITY_STATEMENT",
        "SUPPLEMENTARY",
        "SANITARY",
        "HALAL",
        "CATCH",
        "PHYTOSANITARY",
        "REEXPORT_PHYTOSANITARY",
        "OECD_SEED_VARIETAL",
        "NONCONSIGNMENT",
        "PRODUCT_ESTABLISHMENT_REGISTRATION_DOCUMENT",
        "OTHER_DOCUMENT",
      ],
    }),
    requestorReference: text("requestor_reference"), // Reference from party creating this request, either exporter or a party on behalf of the exporter
    countryId: text("country_id"),
    commodity: text("commodity"),
    // The full user request which initiated this insert.
    request: jsonb("request"),
    /* Add verifiers and certifiers later, and consider if other parties should be split from consignment jsonb to improve data access patterns */
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by"),
    createdById: text("created_by_id"), // sf_contact_id for B2C Users and tc_user_id for AD Users
  },
  (table) => {
    return {
      certificate: uniqueIndex("certificate_request_revision_id").on(
        table.certificateRequestId,
        table.id,
      ),
    }
  },
)

/**
 * The status of a request changes over time, and is independent of
 * updates a user makes to a request. This table is managed by the
 * system, and triggered by changes to the request state, either user
 * initiated, or MPI initiated.
 *
 * Statuses are only inserted, never updated or deleted.
 */
export const certificateRequestStatus = exportCertificateSchema.table(
  "certificate_request_status",
  {
    id: ulidId(),
    certificateRequestRevisionId: varchar("certificate_request_revision_id", {
      length: id_length,
    })
      .notNull()
      .references(() => certificateRequestRevision.id, { onDelete: "cascade" }),
    status: text("status", {
      enum: [
        "DRAFT",
        "RAISED",
        "CANCELLED",
        "AMENDED",
        "VERIFIED",
        "PROVISIONALLY_APPROVED",
        "APPROVED",
        "ACKNOWLEDGED",
        "ACCEPTED",
        "REVOKED",
        "REPLACED",
        "PENDING_REPLACEMENT",
        "REQUESTED_TO_REPLACE",
        "REQUESTED_TO_RESUBMIT",
        "REQUESTED_TO_REVOKE",
      ],
    }).notNull(),
    reasonForStatusChange: text("reason_for_state_change"),
    // Structured replacement reasons (Feature #178801). Wine-specific
    // WineReplacementReason enum values. Nullable; only populated for the
    // Wine replacement flow (REQUESTED_TO_REPLACE / PENDING_REPLACEMENT and the
    // resulting RAISED status of the replacement certificate) via replacement-V2.
    // NOTE: RESUBMIT does not populate this column (free-text reason only).
    reasonCategories: jsonb("reason_categories").$type<string[]>(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by"),
    createdById: text("created_by_id"),
  },
  (table) => {
    return {
      certificateRevision: uniqueIndex(
        "certificate_request_status_revision_id",
      ).on(table.certificateRequestRevisionId, table.id),
    }
  },
)

/**
 * The certificate numbers of raised certificate requests
 * Each raised certificate request has a unique certificate number
 */
export const certificateNumber = exportCertificateSchema.table(
  "certificate_number",
  {
    prefix: text("prefix").notNull(),
    year: integer("year").notNull(),
    number: integer("number").notNull(),
  },
  (table) => {
    return {
      certificateNumberIdx: uniqueIndex("ec_certificate_number_idx").on(
        table.prefix,
        table.year,
      ),
    }
  },
)

/**
 * We track a product and premise over its revisions.
 */
export const product = exportCertificateSchema.table("product_item", {
  id: ulidId(),
  // A product always belongs to a premise. Currently we don't have
  // the concept of premise, so it is simply set to the party id.
  premiseId: varchar("premise_id", {
    length: id_length,
  }).notNull(), // premise id is the sf identifier for the wine company account in ECME, this will be deprecated after the Auth refactor for wine
  registrationId: varchar("registration_id", {
    length: id_length, // could have foreign key on party.registration
  }), // party.registrationId for the wine company registration
  productNumber: text("product_number").notNull(),
  commodity: text("commodity").notNull().default(""),
  netVolumeInLitres: decimal("net_volume_in_litres"),
  manualAdjustmentInLitres: decimal("manual_adj_in_litres"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: text("created_by"),
  createdById: text("created_by_id"),
})

/**
 * If a product is created, we create a revision.
 * If it is updated, we create another revision.
 */
export const productRevision = exportCertificateSchema.table(
  "product_item_revision",
  {
    id: ulidId(),
    productId: varchar("product_item_id", { length: id_length })
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    description: text("description").notNull(),
    inputJson: jsonb("input_json"),
    /* All product fields are subject to review
    common_name: text('common_name'),
    scientific_name: text('scientific_name'),
    production_batch_id: text('production_batch_id'),
    intended_use: text('inteded_use'),
    expiry_date_time: timestamp('expiry_date_time').notNull(),
    net_weight: text('net_weight'),
    gross_weight: text('gross_weight'),
    net_volume: text('net_volume'),
    gross_volume: text('gross_volume'),
    origin_country: jsonb('origin_country'),
    origin_locations: jsonb('origin_locations'),
    hs_code: text('hs_code'),
    brand_name: text('brand_name'),
    commodity: text('commodity'),
    import_permit_number: text('import_permit_number'),
    classifications: jsonb('classifications'),
    physical_packages: jsonb('physical_packages'),
  */
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by"),
    createdById: text("created_by_id"),
  },
  (table) => {
    return {
      product: uniqueIndex("product_item_revision_id").on(
        table.productId,
        table.id,
      ),
    }
  },
)

// DEPRECATED: Refer to product_item_eligibility_criteria_v2
export const productEligibilityCriteria = exportCertificateSchema.table(
  "product_item_eligibility_criteria",
  {
    id: ulidId(),
    productId: varchar("product_item_id", { length: id_length })
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    productRevisionId: varchar("product_item_revision_id", {
      length: id_length,
    })
      .notNull()
      .references(() => productRevision.id, { onDelete: "cascade" }),
    eligibilityCriteriaMet: jsonb("criteria_met"), // B2GEligibilityCriteriaMet
    eligibilityCriteriaFailed: jsonb("criteria_failed"), // B2GEligibilityCriteriaFailed
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
)

export const productEligibilityCriteriaV2 = exportCertificateSchema.table(
  "product_item_eligibility_criteria_v2",
  {
    id: ulidId(),
    productId: varchar("product_item_id", { length: id_length })
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    processId: varchar("process_id", {
      length: id_length,
    }).references(() => productProcess.id, { onDelete: "cascade" }),
    eligibilityCriteria: text("criteria").notNull(),
    market: text("market"),
    wsmpId: text("wsmp_id"),
    met: boolean("met").notNull(),
    failed: boolean("failed").notNull(),
    effectiveDate: timestamp("effective_date", { withTimezone: true }),
    expiryDate: timestamp("expiry_date", { withTimezone: true }),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    active: boolean("active"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => ({
    uniqueProductEligibilityCriteria: unique()
      .on(
        table.productId,
        table.eligibilityCriteria,
        table.processId,
        table.market,
      )
      .nullsNotDistinct(),
  }),
)

export const productEligibilityCriteriaV2History =
  exportCertificateSchema.table(
    "product_item_eligibility_criteria_history",
    {
      id: integer().primaryKey().generatedAlwaysAsIdentity({ startWith: 1 }),
      operation: varchar("operation", { length: 6 }).notNull(),
      eligibilityCriteriaId: varchar("eligibility_criteria_id", {
        length: id_length,
      }),
      productId: varchar("product_item_id", { length: id_length })
        .notNull()
        .references(() => product.id, { onDelete: "cascade" }),
      processId: varchar("process_id", {
        length: id_length,
      }),
      eligibilityCriteria: text("criteria").notNull(),
      market: text("market"),
      wsmpId: text("wsmp_id"),
      met: boolean("met").notNull(),
      failed: boolean("failed").notNull(),
      effectiveDate: timestamp("effective_date", { withTimezone: true }),
      expiryDate: timestamp("expiry_date", { withTimezone: true }),
      updatedAt: timestamp("updated_at", { withTimezone: true })
        .defaultNow()
        .notNull(),
      active: boolean("active"),
      bannedReason: text("banned_reason"),
    },
    (table) => {
      return {
        product: index("eligibility_hst_product_idx").on(table.productId),
      }
    },
  )

/**
 * Holds split relationships for products.
 */
export const productRelation = exportCertificateSchema.table(
  "product_item_relation",
  {
    id: ulidId(),
    fromId: varchar("from_item_id", {
      length: id_length,
    })
      .references(() => product.id, { onDelete: "cascade" })
      .notNull(),
    fromRevisionId: varchar("from_item_revision_id", {
      length: id_length,
    })
      .references(() => productRevision.id, { onDelete: "cascade" })
      .notNull(),
    toId: varchar("to_item_id", { length: id_length })
      .references(() => product.id, { onDelete: "cascade" })
      .notNull(),
    toRevisionId: varchar("to_item_revision_id", {
      length: id_length,
    })
      .references(() => productRevision.id, { onDelete: "cascade" })
      .notNull(),
    relationType: text("relation_type", {
      enum: ["EXPORT"],
    }).default("EXPORT"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by"),
    createdById: text("created_by_id"),
  },
  (table) => {
    return {
      fromToProduct: index("from_to_product_item").on(table.fromId, table.toId),
      fromToProductRevision: index("from_to_product_item_revision").on(
        table.fromRevisionId,
        table.toRevisionId,
      ),
    }
  },
)

export const productEligibilityDeclaration = exportCertificateSchema.table(
  "product_item_eligibility_declaration",
  {
    id: ulidId(),
    productRevisionId: varchar("product_item_revision_id", {
      length: id_length,
    })
      .notNull()
      .references(() => productRevision.id, { onDelete: "cascade" }),
    market: text("market").notNull(),
    criteria: text("criteria").notNull(),
    eligible: boolean("eligible").notNull(),
    expiryDate: timestamp("expiry_date"),
    wsmpId: text("wsmp_id"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
)

export const productEligibility = exportCertificateSchema.table(
  "product_item_eligibility",
  {
    id: ulidId(),
    /**
     * No strategy to keep product_revision and product in sync.
     * Extra work, unlikely to happen, accept as tech debt.
     */
    productId: varchar("product_item_id", { length: id_length })
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    eligibilityCriteriaId: varchar("eligibility_criteria_id", {
      length: id_length,
    }).references(() => productEligibilityCriteria.id, { onDelete: "cascade" }),
    market: text("market").notNull(),
    // future extension to include type of eligibility eg. wine export vs organic
    status: text("status").notNull(),
    effectiveDate: timestamp("effective_date").defaultNow().notNull(),
    expiryDate: timestamp("expiry_date"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => {
    return {
      eligibilityCriteriaId: index("eligibility_criteria_id").on(
        table.eligibilityCriteriaId,
      ),
      eligibilityIdx: uniqueIndex("product_eligibility_idx").on(
        table.eligibilityCriteriaId,
        table.market,
      ),
      eligibilityMarketIdx: index("product_eligibility_market_idx").on(
        table.productId,
        table.market,
        table.id.desc(),
      ),
    }
  },
)

export const productGroup = exportCertificateSchema.table(
  "product_item_group",
  {
    id: ulidId(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by"),
    createdById: text("created_by_id"),
  },
)

/**
 * The items of our consignment, i.e. trade line items.
 */
// TODO: add fields to store container and physicalPackages
// Probably simply store the B2GTradeLineItemInput here
export const productGroupMember = exportCertificateSchema.table(
  "product_item_group_member",
  {
    id: ulidId(),
    productGroupId: varchar("product_item_group_id", {
      length: id_length,
    })
      .notNull()
      .references(() => productGroup.id, { onDelete: "cascade" }),
    productRevisionId: varchar("product_item_revision_id", {
      length: id_length,
    })
      .notNull()
      .references(() => productRevision.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    removedAt: timestamp("removed_at"),
    createdBy: text("created_by"),
    removedBy: text("removed_by"),
    modifiedById: text("modified_by_id"), // can be for removal or creation
    sequence: integer("sequence"),
    sfProductId: text("sf_product_item_id"),
    verified: boolean("verified").notNull().default(false),
    comment: text("comment").notNull().default(""),
  },
  (table) => {
    return {
      productGroupMember: uniqueIndex("product_item_group_member").on(
        table.productGroupId,
        table.productRevisionId,
      ),
      sfProductId: index("sf_product_item")
        .on(
          // cannot be unique, as we don't have the sf_instance in here, although we use that in the query
          table.sfProductId,
        )
        .where(isNotNull(table.sfProductId)),
    }
  },
)

/**
 * Store each supporting file in S3 and store corresponding metadata in
 * this table.
 * The prefix for the ulid here is "sd-".
 */
export const supportingDocumentFile = exportCertificateSchema.table(
  "supporting_document_file",
  {
    id: ulidId(), // ID in S3
    filename: text("filename").notNull(),
    sha256Checksum: varchar("sha256_checksum", {
      length: 64,
    }),
    uploadInProgress: boolean("upload_in_progress").notNull().default(false),
    binaryContent: text("binary_content"), // Base64 encoded binary content
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by"),
    createdById: text("created_by_id"),
    requestorPartyId: text("requestor_party_id").notNull().default(""),
    requestorRegistrationId: varchar("requestor_registration_id", {
      length: id_length, // could have foreign key on party.registration
    }),
    contentType: text("content_type").default(""),
  },
)

/* FUTURE: AP eCert maintains extensive business metadata about supporting documents
and these can be created / managed / referenced using B2G or through the UI.
To support this functionality in future, a new supporting_document table can be added,
which would contain one record per unique document reference number.
To support AP eCert's SD history functionality, a supporting_document_revision shadow
table would contain audit history for all changes to supporting_documents.
Relationships:
  TODAY:  supporting_document_file  1..n  supporting_document_reference (multiple tables)
  FUTURE: supporting_document_file  1..n  supporting_document
          supporting_document       1..n  supporting_document_revision
          supporting_document_file  1..n  supporting_document_revision
          supporting_document       1..n  supporting_document_reference (multiple tables)
*/

/* Each database table, e.g. certificate_request_revision referencing supporting documents
will have a separate reference table created
To update supporting document metadata, create new revision record with updated doc info linked
To remove supporting document, create new revision record without document linked */
export const supportingDocumentReferenceCertificate =
  exportCertificateSchema.table(
    "supporting_document_ref_certificate",
    {
      id: ulidId(),
      certificateRequestRevisionId: varchar("certificate_request_revision_id", {
        length: id_length,
      })
        .notNull()
        .references(() => certificateRequestRevision.id, {
          onDelete: "cascade",
        }),
      supportingDocumentFileId: varchar("supporting_document_file_id", {
        length: id_length,
      })
        .notNull()
        .references(() => supportingDocumentFile.id, { onDelete: "cascade" }),
      documentType: text("document_type"), // Updateable reference data, e.g. FIC, PAC, OTHER
      description: text("description"), // Updateable
      request: jsonb("request"), // B2GReferencedDocumentInput
      createdAt: timestamp("created_at").defaultNow().notNull(),
      createdBy: text("created_by"),
      createdById: text("created_by_id"),
    },
    (table) => {
      return {
        supportingDocumentReferenceIdx: index(
          "ec_supporting_document_ref_certificate",
        ).on(
          table.certificateRequestRevisionId,
          table.supportingDocumentFileId,
        ),
      }
    },
  )

/**
 * Tables used to support our product processes.
 */

/**
 * Store data related to a process.
 */
export const productProcess = exportCertificateSchema.table(
  "process",
  {
    id: ulidId(),
    productId: varchar("product_item_id", { length: id_length })
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    processType: text("process_type").notNull(), // graphql enum ProductProcessType
    wsmpId: varchar("wsmp_id", { length: id_length }), // could have foreign key on party.registration
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by").notNull().default("SYSTEM"),
    createdById: text("created_by_id"),
  },
  (table) => {
    return {
      productProcessProductRevisionIdx: index(
        "product_process_product_revision_idx",
      ).on(table.productId, table.processType),
      uniqueProcessTypeIdx: uniqueIndex(
        "unique_product_packaging_and_winemaking_process",
      )
        .on(table.productId, table.processType)
        .where(
          inArray(table.processType, [
            "WINE_MAKING",
            "BOTTLING_RUN",
            "FLEXI_FILLING",
          ]),
        ),
    }
  },
)

/**
 * If a process is created, we create a revision.
 * Further updates to it create further revisions.
 */
export const productProcessRevision = exportCertificateSchema.table(
  "process_revision",
  {
    id: ulidId(),
    processId: varchar("process_id", { length: id_length })
      .notNull()
      .references(() => productProcess.id, { onDelete: "cascade" }),
    productRevisionId: varchar("product_item_revision_id", {
      length: id_length,
    })
      .notNull()
      .references(() => productRevision.id, { onDelete: "cascade" }),
    netVolumeInLitres: decimal("net_volume_in_litres"),
    processAttributes: jsonb("process_attributes"),
    executionArn: text("execution_arn"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by").notNull().default("SYSTEM"),
    createdById: text("created_by_id"),
  },
  (table) => {
    return {
      process: uniqueIndex("process_revision_idx").on(
        table.processId,
        table.id,
      ),
    }
  },
)

/**
 * Track the tasks on a process that need to be completed
 * for a process to be finished.
 */
export const productProcessTask = exportCertificateSchema.table(
  "process_task",
  {
    id: ulidId(),
    processId: varchar("process_id", { length: id_length })
      .notNull()
      .references(() => productProcess.id, { onDelete: "cascade" }),
    taskType: text("task_type").notNull(),
    // Use to complete this task
    taskToken: text("task_token"),
    taskStatus: text("status", {
      enum: ["ACTIVE", "COMPLETED", "CANCELLED"],
    }),
    assignedTo: text("assigned_to", {
      // posssibility to expand / make more user id driven in the future, just simple for now
      enum: ["EXPORTER", "WSMP_OWNER"],
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by").notNull().default("SYSTEM"),
    createdById: text("created_by_id"),
    modifiedAt: timestamp("modified_at").defaultNow().notNull(),
    // modified by columns only used for datamigration editing updating records
    modifiedBy: text("modified_by"),
  },
  (table) => {
    return {
      productTaskProductProcessIdx: index(
        "product_task_product_process_idx",
      ).on(table.processId, table.taskType),
    }
  },
)

/**
 * NZ Customs supplies us with a list of customs permit numbers that
 * need to be assigned to approved export certificate requests.
 *
 * Customs will provide these numbers which we insert here.
 *
 * They need to be applied in order, and assigned only once.
 */
export const customsPermitNumber = exportCertificateSchema.table(
  "customs_permit_number",
  {
    id: ulidId(),
    permitNumber: text("permit_number").notNull().unique(),
    certificateRequestId: varchar("certificate_request_id", {
      length: id_length,
    })
      .unique()
      .references(() => certificateRequest.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    assignedAt: timestamp("assigned_at"), // Date certifiate_request_id is assigned
  },
)

/**
 * Store data related to a creating a wine sample.
 */
export const wineSample = exportCertificateSchema.table("wine_sample", {
  executionId: text("execution_id").notNull().primaryKey(),
  taskToken: text("task_token"),
  sampleId: text("sample_id").notNull(),
})
